import {
	Badge,
	Box,
	Button,
	FormControl,
	FormLabel,
	HStack,
	Icon,
	Input,
	Link,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Tooltip,
	VStack
} from '@chakra-ui/react';
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { ExternalLink } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';

import { createDeal } from '@external';
import { DepositLinkBadge } from '../ui/deposit-link-badge';
import { formatDealStageLabel } from '@features/crm/quote-verification.helpers';
import { isValidZipcode } from '@helpers';
import { lang } from '@lang';
import type { Project } from '@state';

type ModalStep = 'select' | 'create';

type CustomerData = {
	contactId: string;
	email: string;
	firstName: string;
	lastName: string;
	address: string;
	city: string;
	state: string;
	zipcode: string;
};

type DealSelectionModalProps = {
	isOpen: boolean;
	onClose: () => void;
	deals: Array<Project>;
	currentDealId: string;
	onSelect: (dealId: string) => void;
	customer: CustomerData;
	onDealCreated: (deal: Project) => void;
};

const HUBSPOT_PORTAL_ID = '22371204';

export const DealSelectionModal = (props: DealSelectionModalProps): JSX.Element => {
	const { isOpen, onClose, deals, currentDealId, onSelect, customer, onDealCreated } = props;
	const { getToken } = useKeystoneSession();

	const [modalStep, setModalStep] = useState<ModalStep>('select');
	const [selectedDealId, setSelectedDealId] = useState<string>(currentDealId);
	const [dealName, setDealName] = useState<string>('');
	const [address, setAddress] = useState<string>('');
	const [city, setCity] = useState<string>('');
	const [state, setState] = useState<string>('');
	const [zip, setZip] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen) {
			setSelectedDealId(currentDealId);
			setModalStep('select');
			setError(null);
		}
	}, [isOpen, currentDealId]);

	const handleConfirm = (): void => {
		onSelect(selectedDealId);
	};

	const handleRemoveAssociation = (): void => {
		onSelect('');
	};

	const handleCreateStep = (): void => {
		setDealName(`${customer.firstName} ${customer.lastName}`.trim());
		setAddress(customer.address || '');
		setCity(customer.city || '');
		setState(customer.state || '');
		setZip(customer.zipcode || '');
		setError(null);
		setModalStep('create');
	};

	const handleBack = (): void => {
		setModalStep('select');
		setError(null);
	};

	const handleCreateDeal = async (): Promise<void> => {
		setIsLoading(true);
		setError(null);

		const [result, err] = await createDeal({
			contactId: customer.contactId,
			dealName,
			address: address || undefined,
			city: city || undefined,
			state: state || undefined,
			zip: zip || undefined,
			getToken
		});

		setIsLoading(false);

		if (err || !result) {
			setError(lang.crm.message.createDealFailed);
			return;
		}

		onDealCreated(result);
	};

	const isCreateDisabled = !dealName.trim() || !isValidZipcode(zip);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>
					{modalStep === 'select' ? lang.crm.label.associateDeal : lang.crm.label.createDeal}
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{modalStep === 'select' && (
						<VStack spacing={3} align="stretch">
							{deals.length === 0 && (
								<Text fontSize="sm" color="gray.500">{lang.crm.text.noDealsFound}</Text>
							)}
							{deals.map((deal) => {
								const isSelected = deal.dealId === selectedDealId;
								return (
									<HStack
										key={deal.dealId}
										p={3}
										borderWidth="1px"
										borderRadius="md"
										borderColor={isSelected ? 'blue.400' : 'gray.200'}
										bg={isSelected ? 'blue.50' : 'white'}
										cursor="pointer"
										_hover={{ borderColor: 'blue.300', bg: isSelected ? 'blue.50' : 'gray.50' }}
										onClick={() => setSelectedDealId(deal.dealId)}
										justify="space-between"
									>
										<VStack align="start" spacing={0}>
											<Text fontWeight="bold">{deal.dealName || deal.dealId}</Text>
											<HStack spacing={2} mt={1}>
												<Badge colorScheme="purple" fontSize="xs">{formatDealStageLabel(deal.dealStage)}</Badge>
												<DepositLinkBadge depositLink={deal.depositLink} />
											</HStack>
											<Text fontSize="sm" color="gray.600">
												{[deal.address, deal.city, deal.state, deal.zip].filter(Boolean).join(', ')}
											</Text>
										</VStack>
										<HStack spacing={2}>
											{isSelected && <Badge colorScheme="blue">Selected</Badge>}
											<Tooltip label="Open deal in HubSpot" placement="left">
												<Link
													href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${deal.dealId}`}
													isExternal
													display="inline-flex"
													alignItems="center"
													onClick={(e) => e.stopPropagation()}
												>
													<Icon as={ExternalLink} boxSize={4} color="gray.500" _hover={{ color: 'blue.500' }} />
												</Link>
											</Tooltip>
										</HStack>
									</HStack>
								);
							})}
						</VStack>
					)}
					{modalStep === 'create' && (
						<Box display="flex" flexDirection="column" gap={4}>
							<FormControl isRequired>
								<FormLabel fontSize="sm">{lang.crm.label.dealName}</FormLabel>
								<Input
									type="text"
									size="md"
									value={dealName}
									onChange={(e) => { setDealName(e.target.value); }}
									autoFocus
								/>
							</FormControl>
							<FormControl>
								<FormLabel fontSize="sm">{lang.crm.label.address}</FormLabel>
								<Input
									type="text"
									size="md"
									value={address}
									onChange={(e) => { setAddress(e.target.value); }}
								/>
							</FormControl>
							<FormControl>
								<FormLabel fontSize="sm">{lang.crm.label.city}</FormLabel>
								<Input
									type="text"
									size="md"
									value={city}
									onChange={(e) => { setCity(e.target.value); }}
								/>
							</FormControl>
							<FormControl>
								<FormLabel fontSize="sm">{lang.crm.label.state}</FormLabel>
								<Input
									type="text"
									size="md"
									value={state}
									onChange={(e) => { setState(e.target.value); }}
								/>
							</FormControl>
							<FormControl isRequired>
								<FormLabel fontSize="sm">{lang.crm.label.zip}</FormLabel>
								<Input
									type="text"
									size="md"
									value={zip}
									onChange={(e) => { setZip(e.target.value); }}
								/>
							</FormControl>
							{error && (
								<Text fontSize="sm" color="red.500">{error}</Text>
							)}
						</Box>
					)}
				</ModalBody>
				<ModalFooter>
					{modalStep === 'select' && (
						<>
							<Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
							{selectedDealId && (
								<Button
									colorScheme="red"
									variant="outline"
									mr={3}
									onClick={handleRemoveAssociation}
								>
									Remove Association
								</Button>
							)}
							<Button
								colorScheme="green"
								variant="outline"
								mr={3}
								onClick={handleCreateStep}
							>
								{lang.crm.label.createDeal}
							</Button>
							<Button
								colorScheme="blue"
								onClick={handleConfirm}
								isDisabled={!selectedDealId}
							>
								Save
							</Button>
						</>
					)}
					{modalStep === 'create' && (
						<>
							<Button variant="ghost" mr={3} onClick={handleBack}>Back</Button>
							<Button
								colorScheme="green"
								onClick={handleCreateDeal}
								isLoading={isLoading}
								isDisabled={isCreateDisabled}
							>
								{lang.crm.label.createDeal}
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
